import React, { useState, useContext } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  Loading,
  Modal,
  BreakpointContext,
  PromoContext,
  MobileContext,
} from "components/common";
import { useQuery } from "@apollo/react-hooks";
import { COMPLETION_STATUS } from "utils/constants";
import {
  GET_CREDIT_BOOST_TRANSACTIONS,
  GET_USER_BANK_ACCOUNTS,
  GET_BOOST_PAST_DUE_AMOUNT,
} from "./mutations";
import {
  FinicityConnectBoost,
  Overview,
  MaximizeCredit,
  InviteFriends,
  BillHistory,
} from "./components";
import BoostSelfAdd from "./components/BoostSelfAdd/BoostSelfAddModal";
import {
  boostProductTierConfig,
  tomoCardBankListItem,
} from "./consts";
import "./BoostLandingStyle.css";

const PastDueModal = ({ pastDueModalOpen, setPastDueModalOpen }) => {
  return (
    <Modal
      isOpen={pastDueModalOpen}
      close={() => setPastDueModalOpen(false)}
      hasCrossContainer={false}
      materialModalStyle={{ zIndex: 11 }}
    >
      <div
        className="bg-black-primary text-white rounded-xl"
        style={{ maxWidth: 500 }}
      >
        <div className="mx-4 py-6 flex flex-col justify-center items-center">
          <div className="text-center font-bold text-xl py-2">
            Action Required
          </div>
          <div className="w-10/12 mx-auto text-center">
            To avoid account suspension and the pausing of your credit
            reporting, please initiate a payment by clicking{" "}
            <Link
              to="/settings?section=payment-method"
              className="text-boost-lighter-purple hover:text-boost-light-purple underline hover:underline"
            >
              here
            </Link>
            .
          </div>
          <div className="w-10/12 mx-auto text-center mt-4">
            You may also add a new payment method to submit a payment.
          </div>
        </div>
      </div>
    </Modal>
  );
};

const BoostEnrolled = ({
  user,
  currentProductTier,
  currentProductTerm,
  // isFreeTrial,
}) => {
  const { isBelow } = useContext(BreakpointContext);
  const { promoInfo } = useContext(PromoContext);
  const { isMobile } = useContext(MobileContext);
  const { search } = useLocation();
  const history = useHistory();
  const belowMd = isBelow("md");
  const searchParams = new URLSearchParams(search);
  const showAddAccount = !!searchParams.get("addAccount");

  const [openFinicity, setOpenFinicity] = useState(showAddAccount);
  const [errorMessage, setErrorMessage] = useState(null);
  const [refetching, setRefetching] = useState(false);
  const [showSelfAdd, setShowSelfAdd] = useState(false);

  const currentTierConfig =
    boostProductTierConfig[currentProductTerm]?.[currentProductTier];
  const {
    lookbackPeriod,
    title,
  } = currentTierConfig || {};

  const {
    loading,
    data,
    refetch: refetchBoostTransactions,
  } = useQuery(GET_CREDIT_BOOST_TRANSACTIONS, {
    fetchPolicy: "network-only",
  });
  const transactions = data?.getCreditBoostTransactions?.transactions || [];

  const { data: bankAccountsData, refetch: refetchBank } = useQuery(
    GET_USER_BANK_ACCOUNTS,
    {
      fetchPolicy: "network-only",
    },
  );

  const { data: pastDueData } = useQuery(GET_BOOST_PAST_DUE_AMOUNT);
  const pastDueAmount = pastDueData?.getUserProductStatus?.pastDueAmount;
  const isPastDue = pastDueAmount > 0;
  const [pastDueModalOpen, setPastDueModalOpen] = useState(isPastDue);

  const userBankAccounts = (bankAccountsData?.getUserBankAccounts || [])
    .reduce((acc, bank) => {
      const { institutionName, logoURL, accounts } = bank;
      const accountsWithBankData = accounts.map((a) => ({
        ...a,
        institutionName,
        logoURL,
      }));
      acc.push(...accountsWithBankData);
      return acc;
    }, [])
    .sort((a, b) => {
      // sort by last updated
      const timeA = a.statusCodeUpdatedAt
        ? new Date(a.statusCodeUpdatedAt).getTime()
        : 0;
      const timeB = a.statusCodeUpdatedAt
        ? new Date(b.statusCodeUpdatedAt).getTime()
        : 0;
      return timeB - timeA;
    });

  const hasBeenApprovedBefore = [
    COMPLETION_STATUS.APPROVED,
    COMPLETION_STATUS.TERMINATED,
  ];
  if (hasBeenApprovedBefore.includes(user.applicationStatus)) {
    userBankAccounts.unshift(tomoCardBankListItem);
  }

  if (loading) {
    return <Loading boost />;
  }

  return (
    <div className="h-full" style={{ maxWidth: isMobile ? "100vw" : "calc(100vw - 80px)" }}>
      <PastDueModal
        pastDueModalOpen={pastDueModalOpen}
        setPastDueModalOpen={setPastDueModalOpen}
      />
      {showSelfAdd && (
        <BoostSelfAdd
          lookbackPeriod={lookbackPeriod}
          currentProductTier={title}
          isOpen={showSelfAdd}
          close={(event, reason, openFinicityClicked) => {
            setShowSelfAdd(false);
            if (openFinicityClicked) {
              setOpenFinicity(true);
            }
          }}
        />
      )}
      {errorMessage && (
        <p className="text-red-600 mt-2">{errorMessage}</p>
      )}
      <div className="hidden mdl:flex mdl:flex-row w-full gap-8">
        <div className="flex flex-1 flex-col gap-8">
          <Overview
            numberOfBills={transactions.length}
            numberOfLinkedAccounts={userBankAccounts.length}
          />
          <MaximizeCredit linkedAccount={userBankAccounts.length > 0} />
          <InviteFriends
            referralCode={user.referralCode}
            currentTerm={currentProductTerm}
            currentTier={currentProductTier}
          />
        </div>
        <div className="flex-2 order-2" style={{ maxWidth: "40vw" }}>
          <BillHistory
            AddAccountComponent={
              <FinicityConnectBoost
                openFinicity={openFinicity}
                setOpenFinicity={setOpenFinicity}
                refetchBank={refetchBank}
                refetchBoostTransactions={refetchBoostTransactions}
                setErrorMessage={setErrorMessage}
                refetching={refetching}
                setRefetching={setRefetching}
              />
            }
            accounts={userBankAccounts}
            transactions={transactions}
            setShowSelfAdd={setShowSelfAdd}
          />
        </div>
      </div>

      <div className="flex flex-col mdl:hidden w-full gap-6">
        <Overview
          numberOfBills={transactions.length}
          numberOfLinkedAccounts={userBankAccounts.length}
        />
        <BillHistory
          AddAccountComponent={
            <FinicityConnectBoost
              openFinicity={openFinicity}
              setOpenFinicity={setOpenFinicity}
              refetchBank={refetchBank}
              refetchBoostTransactions={refetchBoostTransactions}
              setErrorMessage={setErrorMessage}
              refetching={refetching}
              setRefetching={setRefetching}
            />
          }
          accounts={userBankAccounts}
          transactions={transactions}
          setShowSelfAdd={setShowSelfAdd}
        />
        <MaximizeCredit linkedAccount={userBankAccounts.length > 0} />
        <InviteFriends
          referralCode={user.referralCode}
          currentTerm={currentProductTerm}
          currentTier={currentProductTier}
        />
      </div>
    </div>
  );
};

export default BoostEnrolled;
